import type { Database } from 'sql.js';
import { queryOne } from './sqljsSqlitePrimitives.ts';

/** Global delivery uniqueness rule violation (same delivery number, provider, or food group). */
export class EntregaClaimConstraintError extends Error {
  /** Initializes the constraint error with a specific message. */

  constructor(message: string) {
    super(message);
    this.name = 'EntregaClaimConstraintError';
  }
}

/** Retrieves the active food group IDs for a given provider from the database. */
export function getGruposAlimentosIdsForProveedor(db: Database, proveedorId: number): number[] {
  if (!Number.isFinite(proveedorId) || proveedorId <= 0) return [];
  const st = db.prepare(
    `SELECT pga.grupo_alimentos_id AS g
     FROM proveedores p
     INNER JOIN proveedor_grupo_alimentos pga ON p.id = pga.proveedor_id
     WHERE p.id = ? AND p.estado = 1`
  );
  const out: number[] = [];
  try {
    st.bind([proveedorId]);
    while (st.step()) {
      const row = st.getAsObject();
      const g = row.g ?? row.G;
      if (g != null) {
        const n = Number(g);
        if (Number.isFinite(n) && n > 0) out.push(n);
      }
    }
  } finally {
    st.free();
  }
  return out;
}

/** Returns the ID if it is a positive number, otherwise null. */
function positiveId(id: number | null): number | null {
  return id != null && id > 0 ? id : null;
}

/** Fails if another delivery point under the same catalog entrega reuses this trimmed delivery number label. */
function assertPuntoEntregaNumeroNoDuplicado(db: Database, partId: number, peId: number): void {
  const dupNum = queryOne(
    db,
    `SELECT pe_o.id FROM punto_entrega_participante pep
     INNER JOIN punto_entrega pe_o ON pep.punto_entrega_id = pe_o.id
     INNER JOIN punto_entrega pe_s ON pe_s.id = ?
     WHERE pep.participante_id = ?
       AND pe_o.id != ? AND pe_o.entrega_id = pe_s.entrega_id
     LIMIT 1`,
    [peId, partId, peId],
  );
  if (dupNum) {
    throw new EntregaClaimConstraintError(
      'Este participante ya tiene un registro para el mismo número de entrega en otro punto de entrega.',
    );
  }
}

/** Fails if the participant already claimed this provider on another point (same catalog entrega) or redemption. */
function assertPuntoEntregaProveedorNoDuplicado(db: Database, partId: number, peId: number, provId: number): void {
  const dupRb = queryOne(
    db,
    `SELECT 1 AS x FROM redencion_bono_participante rbp
     INNER JOIN redenciones_bono rb ON rbp.redencion_bono_id = rb.id
     WHERE rbp.participante_id = ? AND rb.proveedor_id = ?
     LIMIT 1`,
    [partId, provId],
  );
  if (dupRb) {
    throw new EntregaClaimConstraintError('Este participante ya reclamó con este proveedor (en redención).');
  }
}

/** Fails if the participant already claimed this food group on another point (same catalog entrega) or redemption. */
function assertPuntoEntregaGrupoNoDuplicado(db: Database, partId: number, peId: number, grpId: number): void {
  const dupRbG = queryOne(
    db,
    `SELECT 1 AS x FROM redencion_bono_participante rbp
     INNER JOIN redenciones_bono rb ON rbp.redencion_bono_id = rb.id
     INNER JOIN proveedor_grupo_alimentos pga ON rb.proveedor_id = pga.proveedor_id
     WHERE rbp.participante_id = ? AND pga.grupo_alimentos_id = ?
     LIMIT 1`,
    [partId, grpId],
  );
  if (dupRbG) {
    throw new EntregaClaimConstraintError('Este participante ya reclamó este grupo de alimentos (en redención).');
  }
}

/** Delivery point: participant may not appear on another punto for the same catalog `entrega_id`. PE does not model supplier or food-group claims. */
export function assertPuntoEntregaParticipanteCatalogUniqueness(
  db: Database,
  puntoEntregaId: number,
  participanteId: number,
): void {
  const peId = puntoEntregaId;
  const partId = participanteId;
  if (!Number.isFinite(peId) || peId <= 0 || !Number.isFinite(partId) || partId <= 0) return;

  assertPuntoEntregaNumeroNoDuplicado(db, partId, peId);
}

/** Delivery point: unique delivery number; provider and group are global (skips rows from the same point on re-save). */
export function assertPuntoEntregaParticipanteAllowed(
  db: Database,
  args: {
    puntoEntregaId: number;
    participanteId: number;
    proveedorId: number | null;
    grupoAlimentosId: number | null;
  },
): void {
  const peId = args.puntoEntregaId;
  const partId = args.participanteId;
  if (!Number.isFinite(peId) || peId <= 0 || !Number.isFinite(partId) || partId <= 0) return;

  assertPuntoEntregaNumeroNoDuplicado(db, partId, peId);

  const provId = positiveId(args.proveedorId);
  if (provId != null) {
    assertPuntoEntregaProveedorNoDuplicado(db, partId, peId, provId);
  }

  const grpId = positiveId(args.grupoAlimentosId);
  if (grpId != null) {
    assertPuntoEntregaGrupoNoDuplicado(db, partId, peId, grpId);
  }
}

/** Redemption: provider and food-group claims are scoped per catalog entrega (skips same redemption on re-save). */
export function assertRedencionParticipanteAllowed(
  db: Database,
  args: {
    redencionBonoId: number;
    entregaCatalogId: number | null;
    participanteId: number;
    proveedorId: number | null;
    grupoAlimentosId: number | null;
  },
): void {
  const rbId = args.redencionBonoId;
  const partId = args.participanteId;
  const entregaId = positiveId(args.entregaCatalogId);
  if (
    !Number.isFinite(rbId) ||
    rbId <= 0 ||
    !Number.isFinite(partId) ||
    partId <= 0 ||
    entregaId == null
  ) {
    return;
  }

  const provId = positiveId(args.proveedorId);
  if (provId != null) {
    const dupRb = queryOne(
      db,
      `SELECT 1 AS x FROM redencion_bono_participante rbp
       INNER JOIN redenciones_bono rb ON rbp.redencion_bono_id = rb.id
       WHERE rbp.participante_id = ? AND rb.proveedor_id = ? AND rb.entrega_id = ?
         AND rbp.redencion_bono_id != ?
       LIMIT 1`,
      [partId, provId, entregaId, rbId],
    );
    if (dupRb) {
      throw new EntregaClaimConstraintError(
        'Este participante ya reclamó con este proveedor en esta entrega.',
      );
    }
  }

  const grpId = positiveId(args.grupoAlimentosId);
  if (grpId != null) {
    const dupRbG = queryOne(
      db,
      `SELECT 1 AS x FROM redencion_bono_participante rbp
       INNER JOIN redenciones_bono rb ON rbp.redencion_bono_id = rb.id
       INNER JOIN proveedor_grupo_alimentos pga ON rb.proveedor_id = pga.proveedor_id
       WHERE rbp.participante_id = ? AND pga.grupo_alimentos_id = ? AND rb.entrega_id = ?
         AND rbp.redencion_bono_id != ?
       LIMIT 1`,
      [partId, grpId, entregaId, rbId],
    );
    if (dupRbG) {
      throw new EntregaClaimConstraintError(
        'Este participante ya reclamó este grupo de alimentos en esta entrega.',
      );
    }
  }
}
